import type {
  IAngularEvent,
  ILocationService,
  IScope,
  IWindowService,
  route,
} from 'angular';

import type { BuyerAppointmentType } from '@bb/domain/dist/types';

import { theme } from 'components';
import { buildGoogleMapsAddress, detectIOSDevice } from 'utils';
import template from './external_buyer_contact_appointment_list.html';

const isIOSDevice = detectIOSDevice();

class ExternalBuyerContactAppointmentListCtrl {
  public loadMoreAppointments: boolean;
  public appointments: BuyerAppointmentType[] = [];
  public listingDetailTextColor = theme.gray7;
  public appointmentsLoaded = false;
  public iosDrawerVisible = false;
  public destinationAddress = '';

  private dashboardId: string;
  private contactType: string;
  private page = 0;
  private size = 20;

  constructor(
    private $scope: IScope,
    private $location: ILocationService,
    private $routeParams: route.IRouteParamsService,
    private $window: IWindowService,
    private appointmentListsService: any,
    private ExternalAuth: any,
    private ErrorService: any,
  ) {}

  private loadBuyerAppointments() {
    return this.appointmentListsService
      .getExternalBuyerAppointments(this.page, this.size, this.dashboardId)
      .then(({ appointments }: { appointments: BuyerAppointmentType[] }) => {
        this.page += 1;
        this.loadMoreAppointments = appointments.length === this.size;
        this.appointments = this.appointments.concat(appointments);
      })
      .catch((err: Error) => {
        this.ErrorService.toastyHTTPErr(err);
      })
      .finally(() => {
        this.appointmentsLoaded = true;
      });
  }

  private loadCustomContactAppointments() {
    return this.appointmentListsService
      .getExternalCustomContactAppointments(
        this.page,
        this.size,
        this.dashboardId,
      )
      .then(({ appointments }: { appointments: BuyerAppointmentType[] }) => {
        this.page += 1;
        this.loadMoreAppointments = appointments.length === this.size;
        this.appointments = this.appointments.concat(appointments);
      })
      .catch((err: Error) => {
        this.ErrorService.toastyHTTPErr(err);
      })
      .finally(() => {
        this.appointmentsLoaded = true;
      });
  }

  $onInit() {
    this.dashboardId = this.$routeParams.dashboardId;
    this.contactType = this.ExternalAuth.auth.user.contactType;

    if (this.contactType === 'nonagent' || this.contactType === 'customagent') {
      this.loadCustomContactAppointments();
    } else {
      this.loadBuyerAppointments();
    }
  }

  dataLoaded = () => this.appointmentsLoaded;

  loadMore = () => {
    if (this.loadMoreAppointments) {
      if (
        this.contactType === 'nonagent' ||
        this.contactType === 'customagent'
      ) {
        this.loadCustomContactAppointments();
      } else {
        this.loadBuyerAppointments();
      }
    }
  };

  clickAppointment = (appointment: BuyerAppointmentType) => {
    if (appointment.status === 'historical') return;
    this.$location.path(
      `/dash/${this.dashboardId}/appointments/${appointment.id}`,
    );
  };

  clickListing = (clickEvent: IAngularEvent, listingId: string) => {
    clickEvent.stopPropagation();
    this.$location.path(`/dash/${this.dashboardId}/listing/${listingId}`);
  };

  clickDirections = (
    clickEvent: IAngularEvent,
    listing: BuyerAppointmentType['listing'],
  ) => {
    clickEvent.stopPropagation();
    const destinationAddress = buildGoogleMapsAddress(listing);
    if (isIOSDevice) {
      this.destinationAddress = destinationAddress;
      this.iosDrawerVisible = true;
    } else {
      this.$window.open(
        `https://www.google.com/maps/dir/?api=1&destination=${destinationAddress}`,
        '_blank',
      );
    }
  };

  onIosDrawerClose = () => {
    this.$scope.$apply(() => {
      this.iosDrawerVisible = false;
    });
  };
}

angular
  .module('bb.external')
  .component('bbExternalBuyerContactAppointmentList', {
    bindings: {},
    controller: ExternalBuyerContactAppointmentListCtrl,
    controllerAs: 'vm',
    template,
  });
ExternalBuyerContactAppointmentListCtrl.$inject = [
  '$scope',
  '$location',
  '$routeParams',
  '$window',
  'appointmentListsService',
  'ExternalAuth',
  'ErrorService',
];
